import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

// Translation data
const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      projects: 'Projects',
      about: 'About',
      contact: 'Contact',
      faq: 'FAQ',
      privacy: 'Privacy Policy',
    },
    // Home page
    home: {
      badge: 'Founded 2025 • Breda, Netherlands',
      hero: {
        title: 'Deeper Vision. Smarter Health.',
        subtitle: 'Building the next generation of human-centered medical AI.',
      },
      heading: 'Deeper Vision. Smarter Health.',
      subtitle: 'Building the next generation of human-centered medical AI.',
      description: 'We build explainable medical AI systems that combine cutting-edge deep learning with clinical precision. Our research focuses on multi-organ diagnostic intelligence.',
      cta: {
        explore: 'Explore Projects',
        contact: 'Contact Us',
      },
      status: {
        research: 'In R&D',
        vision: 'Vision',
      },
      ourProjects: 'Our Projects',
      poweredBy: 'Powered by Research Excellence',
      foundation: {
        title: 'Our Foundation',
        subtitle: 'Building AI that strengthens medical judgment — not replaces it',
        mission: 'Mission',
        missionText: 'To develop explainable, human-centered AI that empowers physicians with insight — not automation.',
        vision: 'Vision',
        visionText: 'To create Yamai HealthNet by 2035 — a unified diagnostic ecosystem integrating multiple organ-based AI models.',
        values: 'Values',
        valuesText: 'Transparency, ethics, clinical validation, and unwavering commitment to patient privacy and safety.',
      },
      roadmap: {
        title: 'Our Roadmap',
        subtitle: 'From prototype to platform: The journey to 2035',
      },
    },
    // About page
    about: {
      title: 'About Yamai',
      mission: 'Our Mission',
      missionText: 'To develop AI-powered diagnostic systems that are transparent, clinically validated, and human-centered.',
      vision: 'Our Vision',
      visionText: 'A future where AI augments human medical expertise to provide early, accurate diagnoses across multiple specialties.',
      team: 'Our Team',
      futureTeams: 'Future Teams',
      stage: 'Current Stage',
      stageText: 'We are in the research and development phase, building foundational AI models and seeking partnerships with medical institutions.',
    },
    // Projects
    projects: {
      title: 'Our Projects',
      subtitle: 'Seven specialized AI systems working towards a unified health intelligence platform',
      filterAll: 'All',
      filterRnD: 'In R&D',
      filterValidation: 'In Validation',
      filterCommercial: 'Commercial',
      filterVision: 'Vision',
      search: 'Search projects...',
      learnMore: 'Learn More',
      // Project descriptions
      cardio: {
        title: 'Yamai Cardio',
        description: 'An intelligent system combining echocardiography and ECG to detect early wall-motion abnormalities using explainable AI. Our flagship project targeting cardiovascular diagnostics.',
        status: 'In R&D',
      },
      neuro: {
        title: 'Yamai Neuro',
        description: 'AI-powered analysis of brain signals and neural patterns for early detection of neurological conditions. Focus on EEG and MRI interpretation.',
        status: 'In R&D',
      },
      derma: {
        title: 'Yamai Derma',
        description: 'Computer vision-based dermal tissue analysis for early skin condition detection and monitoring using advanced image recognition.',
        status: 'In R&D',
      },
      pulmo: {
        title: 'Yamai Pulmo',
        description: 'Smart respiratory and lung function analysis for comprehensive pulmonary health assessment through X-ray and CT scan analysis.',
        status: 'In R&D',
      },
      vision: {
        title: 'Yamai Vision',
        description: 'Retinal pattern detection and analysis for early diagnosis of eye-related diseases including diabetic retinopathy and glaucoma.',
        status: 'In R&D',
      },
      nutriscan: {
        title: 'Yamai NutriScan',
        description: 'Metabolic and nutritional signal analysis for personalized health insights based on biomarker data and dietary patterns.',
        status: 'In R&D',
      },
      healthnet: {
        title: 'Yamai HealthNet',
        description: 'A unified diagnostic ecosystem integrating multiple organ-based AI models into a holistic health intelligence platform. Our 2035 vision.',
        status: 'Vision',
      },
      elderband: {
        title: 'Yamai ElderBand',
        description: 'Smart safety bracelet for elderly people living alone. Continuous vital signs monitoring with AI-powered fall detection and emergency SOS features.',
        status: 'In R&D',
      },
      fallcam: {
        title: 'Yamai FallCam',
        description: 'AI-powered fall detection camera using computer vision and skeleton AI for privacy-preserving monitoring of elderly people at home.',
        status: 'In R&D',
      },
      heartpatch: {
        title: 'Yamai HeartPatch',
        description: 'Lightweight ECG smart patch for 24/7 heart monitoring with AI-powered analysis of arrhythmias, atrial fibrillation, and cardiac anomalies.',
        status: 'In R&D',
      },
      sleepband: {
        title: 'Yamai SleepBand',
        description: 'Smart sleep analysis bracelet with AI-powered sleep stage detection, sleep apnea monitoring, and personalized sleep quality insights.',
        status: 'In R&D',
      },
      skincam: {
        title: 'Yamai SkinCam',
        description: 'Compact AI camera for dermatological analysis. High-resolution imaging with AI detection of skin cancer risk, moles, and skin conditions.',
        status: 'In R&D',
      },
    },
    // Project Details Pages
    projectDetails: {
      inRnD: 'In R&D',
      overview: 'Overview',
      theProblem: 'The Problem',
      ourSolution: 'Our Solution',
      technology: 'Technology',
      roadmap: 'Development Roadmap',
      impact: 'Expected Impact',
      getInvolved: 'Get Involved',
      interestedText: 'Interested in partnering, investing, or collaborating on this project?',
      contactUs: 'Contact Us',
      backToProjects: 'Back to Projects',
    },
    // Contact
    contact: {
      title: 'Get in Touch',
      subtitle: 'Interested in collaboration, research partnerships, or learning more about our work?',
      form: {
        name: 'Full Name',
        email: 'Email Address',
        organization: 'Organization (Optional)',
        message: 'Your Message',
        send: 'Send Message',
        sending: 'Sending...',
      },
      success: "Thank you! Your message has been sent successfully. We'll get back to you shortly.",
      error: 'Please fill in all required fields.',
      info: {
        location: 'Location',
        locationValue: 'Breda, Netherlands',
        email: 'Email',
      },
    },
    // FAQ
    faq: {
      title: 'Frequently Asked Questions',
      subtitle: 'Everything you need to know about Yamai',
      questions: [
        {
          q: 'What is Yamai?',
          a: 'Yamai B.V. is a Dutch medical AI research company focused on developing explainable, multi-organ diagnostic systems. We are currently in the R&D phase, building foundational AI models.',
        },
        {
          q: 'Are your systems clinically validated?',
          a: 'We are in the research and development phase. Our systems are not yet clinically validated or approved for medical use. We are actively seeking partnerships with medical institutions for clinical validation.',
        },
        {
          q: 'What makes Yamai different?',
          a: 'Our focus on explainable AI (XAI) ensures that our diagnostic systems are transparent and interpretable by medical professionals. We also take a holistic approach, developing systems across multiple organ systems rather than focusing on a single specialty.',
        },
        {
          q: 'Can I use your systems now?',
          a: 'No, our systems are currently under development and not available for commercial or clinical use. We are in the research phase and will announce when products are ready for testing or deployment.',
        },
        {
          q: 'How can I collaborate with Yamai?',
          a: 'We welcome partnerships with hospitals, research institutions, and investors. Please contact us through our contact form with details about your organization and interest.',
        },
        {
          q: 'Where is Yamai based?',
          a: 'Yamai B.V. is based in Breda, Netherlands. We are a Dutch-registered company.',
        },
      ],
    },
    // Privacy
    privacy: {
      title: 'Privacy Policy',
      lastUpdated: 'Last updated',
      intro: 'At Yamai B.V., we respect your privacy and are committed to protecting your personal data. This privacy policy explains how we collect, use, and protect your information.',
    },
    // 404
    notFound: {
      title: '404 - Page Not Found',
      subtitle: "The page you're looking for doesn't exist or has been moved.",
      backHome: 'Back to Home',
      exploreProjects: 'Explore Projects',
    },
    // Footer
    footer: {
      tagline: 'Building the next generation of human-centered medical AI.',
      quickLinks: 'Quick Links',
      legal: 'Legal',
      allRights: 'All rights reserved.',
      madeWith: 'Made with',
      in: 'in',
      netherlands: 'the Netherlands',
    },
  },
  nl: {
    // Navigation
    nav: {
      home: 'Home',
      projects: 'Projecten',
      about: 'Over Ons',
      contact: 'Contact',
      faq: 'Veelgestelde Vragen',
      privacy: 'Privacybeleid',
    },
    // Home page
    home: {
      badge: 'Opgericht 2025 • Breda, Nederland',
      hero: {
        title: 'Dieper Inzicht. Slimmere Gezondheid.',
        subtitle: 'We bouwen de volgende generatie mens-gerichte medische AI.',
      },
      heading: 'Dieper Inzicht. Slimmere Gezondheid.',
      subtitle: 'We bouwen de volgende generatie mens-gerichte medische AI.',
      description: 'We ontwikkelen verklaarbare medische AI-systemen die geavanceerd deep learning combineren met klinische precisie. Ons onderzoek richt zich op multi-orgaan diagnostische intelligentie.',
      cta: {
        explore: 'Bekijk Projecten',
        contact: 'Neem Contact Op',
      },
      status: {
        research: 'In O&O',
        vision: 'Visie',
      },
      ourProjects: 'Onze Projecten',
      poweredBy: 'Mogelijk Gemaakt door Onderzoeksexcellentie',
      foundation: {
        title: 'Onze Basis',
        subtitle: 'AI bouwen die medisch oordeel versterkt — niet vervangt',
        mission: 'Missie',
        missionText: 'Verklaarbare, mens-gerichte AI ontwikkelen die artsen empowert met inzicht — niet automatisering.',
        vision: 'Visie',
        visionText: 'Yamai HealthNet creëren tegen 2035 — een verenigd diagnostisch ecosysteem dat meerdere orgaan-gebaseerde AI-modellen integreert.',
        values: 'Waarden',
        valuesText: 'Transparantie, ethiek, klinische validatie, en onwrikbare toewijding aan patiëntenprivacy en veiligheid.',
      },
      roadmap: {
        title: 'Ons Stappenplan',
        subtitle: 'Van prototype tot platform: De reis naar 2035',
      },
    },
    // About page
    about: {
      title: 'Over Yamai',
      mission: 'Onze Missie',
      missionText: 'AI-aangedreven diagnostische systemen ontwikkelen die transparant, klinisch gevalideerd en mens-gericht zijn.',
      vision: 'Onze Visie',
      visionText: 'Een toekomst waarin AI de menselijke medische expertise versterkt om vroege, nauwkeurige diagnoses te stellen over meerdere specialismen.',
      team: 'Ons Team',
      futureTeams: 'Toekomstige Teams',
      stage: 'Huidige Fase',
      stageText: 'We bevinden ons in de onderzoeks- en ontwikkelingsfase en bouwen fundamentele AI-modellen terwijl we partnerschappen zoeken met medische instellingen.',
    },
    // Projects
    projects: {
      title: 'Onze Projecten',
      subtitle: 'Zeven gespecialiseerde AI-systemen die werken aan een verenigd gezondheidsintelligentieplatform',
      filterAll: 'Alle',
      filterRnD: 'In O&O',
      filterValidation: 'In Validatie',
      filterCommercial: 'Commercieel',
      filterVision: 'Visie',
      search: 'Zoek projecten...',
      learnMore: 'Lees Meer',
      // Project descriptions
      cardio: {
        title: 'Yamai Cardio',
        description: 'Een intelligent systeem dat echocardiografie en ECG combineert om vroege wandbewegingsafwijkingen te detecteren met behulp van verklaarbare AI. Ons vlaggenschipproject gericht op cardiovasculaire diagnostiek.',
        status: 'In O&O',
      },
      neuro: {
        title: 'Yamai Neuro',
        description: 'AI-aangedreven analyse van hersenensignalen en neurale patronen voor vroege detectie van neurologische aandoeningen. Focus op EEG- en MRI-interpretatie.',
        status: 'In O&O',
      },
      derma: {
        title: 'Yamai Derma',
        description: 'Computer vision-gebaseerde dermale weefselanalyse voor vroege detectie en monitoring van huidaandoeningen met behulp van geavanceerde beeldherkenning.',
        status: 'In O&O',
      },
      pulmo: {
        title: 'Yamai Pulmo',
        description: 'Slimme respiratoire en longfunctieanalyse voor uitgebreide pulmonale gezondheidsbeoordeling via röntgen- en CT-scananalyse.',
        status: 'In O&O',
      },
      vision: {
        title: 'Yamai Vision',
        description: 'Retinale patroondetectie en -analyse voor vroege diagnose van ooggerelateerde ziekten, waaronder diabetische retinopathie en glaucoom.',
        status: 'In O&O',
      },
      nutriscan: {
        title: 'Yamai NutriScan',
        description: 'Metabole en voedingssignaalanalyse voor gepersonaliseerde gezondheidsinzichten op basis van biomarkergegevens en voedingspatronen.',
        status: 'In O&O',
      },
      healthnet: {
        title: 'Yamai HealthNet',
        description: 'Een verenigd diagnostisch ecosysteem dat meerdere orgaan-gebaseerde AI-modellen integreert in een holistisch gezondheidsintelligentieplatform. Onze 2035-visie.',
        status: 'Visie',
      },
      elderband: {
        title: 'Yamai ElderBand',
        description: 'Slimme veiligheidsarmband voor ouderen die alleen wonen. Continue monitoring van vitale functies met AI-aangedreven valdetectie en nood-SOS-functies.',
        status: 'In O&O',
      },
      fallcam: {
        title: 'Yamai FallCam',
        description: 'AI-aangedreven valdetectiecamera met computer vision en skeleton AI voor privacybeschermende monitoring van ouderen thuis.',
        status: 'In O&O',
      },
      heartpatch: {
        title: 'Yamai HeartPatch',
        description: 'Lichtgewicht ECG-slimme patch voor 24/7 hartmonitoring met AI-aangedreven analyse van aritmieën, atriumfibrilleren en hartafwijkingen.',
        status: 'In O&O',
      },
      sleepband: {
        title: 'Yamai SleepBand',
        description: 'Slimme slaapanalysearmband met AI-aangedreven slaapfasedetectie, slaapapneu-monitoring en gepersonaliseerde slaapkwaliteitsinzichten.',
        status: 'In O&O',
      },
      skincam: {
        title: 'Yamai SkinCam',
        description: 'Compacte AI-camera voor dermatologische analyse. Hoge resolutie beeldvorming met AI-detectie van huidkankerrisico, moedervlekken en huidaandoeningen.',
        status: 'In O&O',
      },
    },
    // Project Details Pages
    projectDetails: {
      inRnD: 'In O&O',
      overview: 'Overzicht',
      theProblem: 'Het Probleem',
      ourSolution: 'Onze Oplossing',
      technology: 'Technologie',
      roadmap: 'Ontwikkelingstraject',
      impact: 'Verwachte Impact',
      getInvolved: 'Doe Mee',
      interestedText: 'Geïnteresseerd in partneren, investeren of samenwerken aan dit project?',
      contactUs: 'Neem Contact Op',
      backToProjects: 'Terug naar Projecten',
    },
    // Contact
    contact: {
      title: 'Neem Contact Op',
      subtitle: 'Geïnteresseerd in samenwerking, onderzoekspartnerschappen of meer informatie over ons werk?',
      form: {
        name: 'Volledige Naam',
        email: 'E-mailadres',
        organization: 'Organisatie (Optioneel)',
        message: 'Uw Bericht',
        send: 'Verstuur Bericht',
        sending: 'Verzenden...',
      },
      success: 'Bedankt! Uw bericht is succesvol verzonden. We nemen binnenkort contact met u op.',
      error: 'Vul alle verplichte velden in.',
      info: {
        location: 'Locatie',
        locationValue: 'Breda, Nederland',
        email: 'E-mail',
      },
    },
    // FAQ
    faq: {
      title: 'Veelgestelde Vragen',
      subtitle: 'Alles wat u moet weten over Yamai',
      questions: [
        {
          q: 'Wat is Yamai?',
          a: 'Yamai B.V. is een Nederlands medisch AI-onderzoeksbedrijf gericht op het ontwikkelen van verklaarbare, multi-orgaan diagnostische systemen. We bevinden ons momenteel in de O&O-fase en bouwen fundamentele AI-modellen.',
        },
        {
          q: 'Zijn uw systemen klinisch gevalideerd?',
          a: 'We bevinden ons in de onderzoeks- en ontwikkelingsfase. Onze systemen zijn nog niet klinisch gevalideerd of goedgekeurd voor medisch gebruik. We zoeken actief partnerschappen met medische instellingen voor klinische validatie.',
        },
        {
          q: 'Wat maakt Yamai anders?',
          a: 'Onze focus op verklaarbare AI (XAI) zorgt ervoor dat onze diagnostische systemen transparant en interpreteerbaar zijn voor medische professionals. We hanteren ook een holistische aanpak door systemen te ontwikkelen voor meerdere orgaansystemen in plaats van één specialisme.',
        },
        {
          q: 'Kan ik uw systemen nu gebruiken?',
          a: 'Nee, onze systemen zijn momenteel in ontwikkeling en niet beschikbaar voor commercieel of klinisch gebruik. We bevinden ons in de onderzoeksfase en zullen aankondigen wanneer producten klaar zijn voor testen of implementatie.',
        },
        {
          q: 'Hoe kan ik samenwerken met Yamai?',
          a: 'We verwelkomen partnerschappen met ziekenhuizen, onderzoeksinstellingen en investeerders. Neem contact met ons op via ons contactformulier met details over uw organisatie en interesse.',
        },
        {
          q: 'Waar is Yamai gevestigd?',
          a: 'Yamai B.V. is gevestigd in Breda, Nederland. We zijn een in Nederland geregistreerd bedrijf.',
        },
      ],
    },
    // Privacy
    privacy: {
      title: 'Privacybeleid',
      lastUpdated: 'Laatst bijgewerkt',
      intro: 'Bij Yamai B.V. respecteren we uw privacy en zijn we toegewijd aan het beschermen van uw persoonlijke gegevens. Dit privacybeleid legt uit hoe we uw informatie verzamelen, gebruiken en beschermen.',
    },
    // 404
    notFound: {
      title: '404 - Pagina Niet Gevonden',
      subtitle: 'De pagina die u zoekt bestaat niet of is verplaatst.',
      backHome: 'Terug naar Home',
      exploreProjects: 'Bekijk Projecten',
    },
    // Footer
    footer: {
      tagline: 'We bouwen de volgende generatie mens-gerichte medische AI.',
      quickLinks: 'Snelle Links',
      legal: 'Juridisch',
      allRights: 'Alle rechten voorbehouden.',
      madeWith: 'Gemaakt met',
      in: 'in',
      netherlands: 'Nederland',
    },
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check localStorage first
    const saved = localStorage.getItem('yamai-language');
    if (saved && (saved === 'en' || saved === 'nl')) {
      return saved;
    }
    // Detect browser language
    const browserLang = navigator.language.toLowerCase();
    if (browserLang.startsWith('nl')) {
      return 'nl';
    }
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('yamai-language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return key; // Return key if translation not found
      }
    }
    
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'nl' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};
